import listingsData from "./listingsData.json";

export const HERO_IMAGE =
  "https://images.unsplash.com/photo-1592919505780-303950717480?auto=format&fit=crop&q=80";

export const STATES = [
  { code: "al", name: "Alabama" }, { code: "ak", name: "Alaska" }, { code: "az", name: "Arizona" },
  { code: "ar", name: "Arkansas" }, { code: "ca", name: "California" }, { code: "co", name: "Colorado" },
  { code: "ct", name: "Connecticut" }, { code: "de", name: "Delaware" }, { code: "fl", name: "Florida" },
  { code: "ga", name: "Georgia" }, { code: "hi", name: "Hawaii" }, { code: "id", name: "Idaho" },
  { code: "il", name: "Illinois" }, { code: "in", name: "Indiana" }, { code: "ia", name: "Iowa" },
  { code: "ks", name: "Kansas" }, { code: "ky", name: "Kentucky" }, { code: "la", name: "Louisiana" },
  { code: "me", name: "Maine" }, { code: "md", name: "Maryland" }, { code: "ma", name: "Massachusetts" },
  { code: "mi", name: "Michigan" }, { code: "mn", name: "Minnesota" }, { code: "ms", name: "Mississippi" },
  { code: "mo", name: "Missouri" }, { code: "mt", name: "Montana" }, { code: "ne", name: "Nebraska" },
  { code: "nv", name: "Nevada" }, { code: "nh", name: "New Hampshire" }, { code: "nj", name: "New Jersey" },
  { code: "nm", name: "New Mexico" }, { code: "ny", name: "New York" }, { code: "nc", name: "North Carolina" },
  { code: "nd", name: "North Dakota" }, { code: "oh", name: "Ohio" }, { code: "ok", name: "Oklahoma" },
  { code: "or", name: "Oregon" }, { code: "pa", name: "Pennsylvania" }, { code: "ri", name: "Rhode Island" },
  { code: "sc", name: "South Carolina" }, { code: "sd", name: "South Dakota" }, { code: "tn", name: "Tennessee" },
  { code: "tx", name: "Texas" }, { code: "ut", name: "Utah" }, { code: "vt", name: "Vermont" },
  { code: "va", name: "Virginia" }, { code: "wa", name: "Washington" }, { code: "wv", name: "West Virginia" },
  { code: "wi", name: "Wisconsin" }, { code: "wy", name: "Wyoming" },
];

export const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export const slugifyCity = (c) =>
  String(c || "").toLowerCase().replace(/[.']/g, "").replace(/\s+/g, "-");

export const slugifyState = (name) => slugify(name);

// Find state by code ("al"), full name ("Alabama"), or slug ("alabama", "new-york")
export const stateByCode = (codeOrSlug) => {
  if (!codeOrSlug) return null;
  const clean = String(codeOrSlug).toLowerCase().trim();
  return (
    STATES.find(
      (s) =>
        s.code === clean ||
        slugify(s.name) === clean ||
        s.name.toLowerCase() === clean
    ) || null
  );
};

export const getStateSlug = (stateCodeOrName) => {
  const st = stateByCode(stateCodeOrName);
  return st ? slugify(st.name) : slugify(stateCodeOrName) || "us";
};

export const getStateName = (stateCodeOrName) => {
  const st = stateByCode(stateCodeOrName);
  return st ? st.name : String(stateCodeOrName || "");
};

export const LISTINGS = listingsData;

export const LISTING_BY_SLUG = LISTINGS.reduce((acc, item) => {
  acc[item.slug.toLowerCase()] = item;
  return acc;
}, {});

export const LISTING_BY_CITY = LISTINGS.reduce((acc, item) => {
  const code = item.stateCode.toLowerCase();
  acc[`${code}:${item.citySlug.toLowerCase()}`] = item;
  acc[`${code}:${item.cityName.toLowerCase()}`] = item;
  return acc;
}, {});

export const getListingBySlug = (slug) => {
  if (!slug) return null;
  const clean = String(slug)
    .toLowerCase()
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .replace(/^listing\//, "");

  if (LISTING_BY_SLUG[clean]) return LISTING_BY_SLUG[clean];

  // Try matches in LISTINGS
  const match = LISTINGS.find(
    (item) =>
      item.slug.toLowerCase() === clean ||
      item.citySlug.toLowerCase() === clean ||
      item.cityName.toLowerCase() === clean ||
      clean.includes(item.citySlug.toLowerCase()) ||
      clean.replace(/-/g, " ") === item.cityName.toLowerCase()
  );
  if (match) return match;

  return null;
};

export const getListingForCity = (stateCodeOrName, city) => {
  if (!stateCodeOrName || !city) return null;
  const st = stateByCode(stateCodeOrName);
  const code = st ? st.code.toLowerCase() : String(stateCodeOrName).toLowerCase();
  const cSlug = slugifyCity(city);
  const cName = String(city).toLowerCase().trim();
  return (
    LISTING_BY_CITY[`${code}:${cSlug}`] ||
    LISTING_BY_CITY[`${code}:${cName}`] ||
    null
  );
};

export const getStateUrl = (stateCodeOrName) => {
  const slug = getStateSlug(stateCodeOrName);
  return `/state/${slug}`;
};

export const getCityUrl = (stateCodeOrName, city) => {
  const stateSlug = getStateSlug(stateCodeOrName);
  return `/state/${stateSlug}/${slugifyCity(city)}`;
};

export const getListingUrl = (slugOrListing) => {
  if (!slugOrListing) return "/";
  const listing = typeof slugOrListing === "object" ? slugOrListing : getListingBySlug(slugOrListing);
  if (listing) {
    const stateSlug = getStateSlug(listing.stateCode || listing.state);
    return `/state/${stateSlug}/${slugifyCity(listing.cityName || listing.citySlug)}`;
  }
  return "/";
};

export const getCourseUrl = (course) => {
  if (!course) return "/";
  const stateSlug = getStateSlug(course.state) || "us";
  const slug = course.slug || slugify(course.name) || course.id;
  return `/state/${stateSlug}/course/${slug}`;
};

export const getCleanDescription = (course) => {
  if (!course) return "";
  const d = (course.description || "").trim();
  const stName = getStateName(course.state) || "";

  // Only fall back to template if description is completely empty or is solely a raw image asset URL
  const isOnlyImageUrl =
    d.startsWith("http") &&
    !d.includes(" ") &&
    /\.(png|jpg|jpeg|webp|svg|gif)($|\?)/i.test(d);

  if (!d || isOnlyImageUrl) {
    return `Find ${course.name}, an exciting mini golf destination in ${course.city}${
      stName ? `, ${stName}` : ""
    }. The course carries a ${course.rating || 4.5}-star rating from ${
      course.reviewCount || 0
    } verified reviews, offering creative obstacles, family-friendly putt-putt challenges, and memorable fun for all ages.`;
  }
  return d;
};

